import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import api from '../../../api';
import { toast } from '../../../utils/toast';
import { ModernButton, ModernCard } from '../../ui/modern';
import FieldPalette from './FieldPalette';
import FormPreview from './FormPreview';
import FieldEditor from './FieldEditor';

function buildNewField(type, index, entityTypeId) {
  return {
    id: `new-${Date.now()}-${index}`,
    entityTypeId,
    fieldKey: '',
    label: '',
    fieldType: type,
    placeholder: '',
    defaultValue: null,
    options: null,
    validationRules: null,
    sortOrder: (index + 1) * 10,
    isRequired: false,
    isActive: true,
    conditionalLogic: null,
    helpText: null,
    group: null,
    _isNew: true,
  };
}

export default function FormBuilder() {
  const [sectors, setSectors] = useState([]);
  const [sectorId, setSectorId] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [entityTypeId, setEntityTypeId] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function fetchSectors() {
    try {
      const res = await api.get('/admin/sectors');
      const list = res.data || [];
      setSectors(list);
      const def = list.find((s) => s.isDefault) || list[0];
      setSectorId(def?.id ?? null);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load sectors');
    }
  }

  const fetchEntityTypes = useCallback(async (nextSectorId) => {
    if (!nextSectorId) return;
    try {
      const res = await api.get('/admin/entity-types', { params: { sectorId: nextSectorId, includeInactive: true } });
      const list = res.data || [];
      setEntityTypes(list);
      if (list.length && !entityTypeId) {
        setEntityTypeId(list[0].id);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load entity types');
    }
  }, [entityTypeId]);

  async function fetchFields(nextEntityTypeId) {
    if (!nextEntityTypeId) return;
    setLoading(true);
    try {
      const res = await api.get('/admin/field-definitions', { params: { entityTypeId: nextEntityTypeId, includeInactive: true } });
      const list = res.data || [];
      setFields(list);
      setSelectedFieldId(list[0]?.id || null);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSectors();
  }, []);

  useEffect(() => {
    if (sectorId) fetchEntityTypes(sectorId);
  }, [sectorId, fetchEntityTypes]);

  useEffect(() => {
    if (entityTypeId) fetchFields(entityTypeId);
  }, [entityTypeId]);

  function handleAddField(type) {
    const next = buildNewField(type, fields.length, entityTypeId);
    setFields((prev) => [...prev, next]);
    setSelectedFieldId(next.id);
  }

  function handleSelectField(field) {
    setSelectedFieldId(field.id);
  }

  function handleUpdateField(updated) {
    setFields((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  }

  async function handleRemoveField(field) {
    if (field._isNew) {
      setFields((prev) => prev.filter((f) => f.id !== field.id));
      return;
    }
    if (!window.confirm(`Disable field "${field.label}"?`)) return;
    try {
      await api.delete(`/admin/field-definitions/${field.id}`);
      setFields((prev) => prev.filter((f) => f.id !== field.id));
      toast.success('Field disabled');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to disable field');
    }
  }

  function handleReorder(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFields((items) => {
      const oldIndex = items.findIndex((f) => f.id === active.id);
      const newIndex = items.findIndex((f) => f.id === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex).map((f, idx) => ({
        ...f,
        sortOrder: (idx + 1) * 10,
      }));
      return reordered;
    });
  }

  async function handleSaveAll() {
    setSaving(true);
    try {
      const newFields = fields.filter((f) => f._isNew);
      const existing = fields.filter((f) => !f._isNew);

      for (const field of newFields) {
        const payload = { ...field, entityTypeId, sortOrder: Number(field.sortOrder) || 0 };
        delete payload.id;
        delete payload._isNew;
        await api.post('/admin/field-definitions', payload);
      }

      for (const field of existing) {
        const payload = { ...field, entityTypeId, sortOrder: Number(field.sortOrder) || 0 };
        delete payload._isNew;
        await api.put(`/admin/field-definitions/${field.id}`, payload);
      }

      const orders = fields.map((f, idx) => ({ id: f.id, sortOrder: (idx + 1) * 10 })).filter((f) => String(f.id).startsWith('new-') === false);
      if (orders.length) {
        await api.post('/admin/field-definitions/reorder', { orders });
      }

      toast.success('Fields saved');
      await fetchFields(entityTypeId);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save fields');
    } finally {
      setSaving(false);
    }
  }

  const sectorOptions = sectors.map((s) => ({ value: String(s.id), label: `${s.name}${s.isDefault ? ' (Default)' : ''}` }));
  const entityTypeOptions = entityTypes.map((t) => ({ value: String(t.id), label: `${t.name} (${t.code})` }));
  const selectedField = fields.find((f) => f.id === selectedFieldId) || null;
  const fieldOptions = useMemo(() => fields.map((f) => ({ value: f.fieldKey, label: f.label || f.fieldKey || 'field' })), [fields]);

  return (
    <ModernCard>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Form Builder</h2>
          <p className="text-gray-600 dark:text-slate-400 text-sm">Design dynamic fields for each entity type</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="min-w-[240px]">
            <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Sector</label>
            <select
              value={sectorId ?? ''}
              onChange={(e) => setSectorId(Number(e.target.value))}
              className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
            >
              {sectorOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-slate-800 text-white">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[260px]">
            <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Entity Type</label>
            <select
              value={entityTypeId ?? ''}
              onChange={(e) => setEntityTypeId(Number(e.target.value))}
              className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
            >
              {entityTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-slate-800 text-white">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <ModernButton variant="primary" onClick={handleSaveAll} disabled={saving || !entityTypeId}>
            {saving ? 'Saving…' : 'Save All'}
          </ModernButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <FieldPalette onAddField={handleAddField} />
        </div>
        <div className="lg:col-span-1">
          {loading ? (
            <div className="text-gray-600 dark:text-slate-400">Loading fields…</div>
          ) : (
            <FormPreview
              fields={fields}
              selectedFieldId={selectedFieldId}
              onSelectField={handleSelectField}
              onRemoveField={handleRemoveField}
              onReorder={handleReorder}
            />
          )}
        </div>
        <div className="lg:col-span-1">
          <FieldEditor
            field={selectedField}
            onChange={handleUpdateField}
            fieldOptions={fieldOptions}
          />
        </div>
      </div>
    </ModernCard>
  );
}


